import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import type { AgentTemplateWizardStepType, WizardStepProps } from '../types';

/**
 * Generic `text` template step (design §10.3) — for future `custom` templates.
 * Label = step title; Input/Textarea switch on `options.multiline`; placeholder
 * and helper from `options.*`. The value lives in `draft.steps[step.id]` and is
 * ADVISORY in P2 (shown under review "Additional answers", never invented into
 * the create body — §25 Q3).
 */
function GenericTextStep({ step, value, onChange, errors, onErrors }: WizardStepProps) {
  const options = (step.options ?? {}) as {
    multiline?: boolean;
    placeholder?: string;
    helper?: string;
  };
  const text = typeof value === 'string' ? value : '';
  const fieldId = `step-${step.id}`;

  const handleChange = (next: string) => {
    onChange(next);
    if (next.trim() !== '') {
      onErrors({});
    }
  };

  const handleBlur = () => {
    if (step.required && text.trim() === '') {
      onErrors({ value: 'Enter a value.' });
    } else {
      onErrors({});
    }
  };

  const invalid = Boolean(errors.value);
  const describedBy = invalid
    ? `${fieldId}-error`
    : options.helper
      ? `${fieldId}-helper`
      : undefined;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={fieldId}>{step.title}</Label>
      {options.multiline ? (
        <Textarea
          id={fieldId}
          value={text}
          onChange={(event) => handleChange(event.target.value)}
          onBlur={handleBlur}
          placeholder={options.placeholder}
          className="min-h-40 resize-y"
          aria-invalid={invalid ? true : undefined}
          aria-describedby={describedBy}
        />
      ) : (
        <Input
          id={fieldId}
          value={text}
          onChange={(event) => handleChange(event.target.value)}
          onBlur={handleBlur}
          placeholder={options.placeholder}
          aria-invalid={invalid ? true : undefined}
          aria-describedby={describedBy}
        />
      )}
      {options.helper ? (
        <p id={`${fieldId}-helper`} className="text-sm text-muted-foreground">
          {options.helper}
        </p>
      ) : null}
      {invalid ? (
        <p id={`${fieldId}-error`} className="text-sm text-destructive">
          {errors.value}
        </p>
      ) : null}
    </div>
  );
}

export const type: AgentTemplateWizardStepType = 'text';
export const component = GenericTextStep;
